import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TASK_MODULES } from '@/common/constants/constants';
import { questionQueryKeys } from '@/entities/question/question.keys';
import {
  getFinancialInstitutions,
  getFilledDataEntries,
  getOtherCreditCards,
  getOtherFinancialLiabilities,
  getQuestionList,
  submitAnswers,
} from '@/entities/question/question.api';
import { useInvalidateDecedentQueries } from '@/entities/decedent';

export const useQuestionListQuery = (section, decedentId, options = {}) =>
  useQuery({
    queryKey: questionQueryKeys.list(section, decedentId),
    queryFn: () => getQuestionList(section, decedentId),
    enabled: !!section && !!decedentId,
    ...options,
  });

export const useFilledDataEntriesQuery = (decedentId, options = {}) =>
  useQuery({
    queryKey: questionQueryKeys.filledDataEntries(decedentId),
    queryFn: () => getFilledDataEntries(decedentId),
    enabled: !!decedentId,
    ...options,
  });

export const useFinancialInstitutionsQuery = (decedentId, options = {}) =>
  useQuery({
    queryKey: questionQueryKeys.financialInstitutions(decedentId),
    queryFn: () => getFinancialInstitutions(decedentId),
    enabled: !!decedentId,
    ...options,
  });

export const useOtherFinancialLiabilitiesQuery = (decedentId, options = {}) =>
  useQuery({
    queryKey: questionQueryKeys.otherFinancialLiabilities(decedentId),
    queryFn: () => getOtherFinancialLiabilities(decedentId),
    enabled: !!decedentId,
    ...options,
  });

export const useOtherCreditCardsQuery = (decedentId, options = {}) =>
  useQuery({
    queryKey: questionQueryKeys.otherCreditCards(decedentId),
    queryFn: () => getOtherCreditCards(decedentId),
    enabled: !!decedentId,
    ...options,
  });

export const useSubmitAnswersMutation = (options = {}) => {
  const queryClient = useQueryClient();
  const { invalidateDecedent, invalidateTaskList } =
    useInvalidateDecedentQueries();
  const { onSuccess, ...mutationOptions } = options;

  return useMutation({
    mutationFn: submitAnswers,
    onSuccess: async (data, variables, context) => {
      const { decedentId, section } = variables;

      if (section && decedentId) {
        await queryClient.invalidateQueries({
          queryKey: questionQueryKeys.list(section, decedentId),
        });
      }

      if (decedentId) {
        await queryClient.invalidateQueries({
          queryKey: questionQueryKeys.filledDataEntries(decedentId),
        });
        await queryClient.invalidateQueries({
          queryKey: questionQueryKeys.financialInstitutions(decedentId),
        });
        await queryClient.invalidateQueries({
          queryKey: questionQueryKeys.otherFinancialLiabilities(decedentId),
        });
        await queryClient.invalidateQueries({
          queryKey: questionQueryKeys.otherCreditCards(decedentId),
        });
      }

      if (
        decedentId &&
        [TASK_MODULES.IMMEDIATE, 'basic_document_submission'].includes(section)
      ) {
        await invalidateDecedent(decedentId);
      }

      if (decedentId && Object.values(TASK_MODULES).includes(section)) {
        await invalidateTaskList(decedentId, section);
        await invalidateDecedent(decedentId);
      }

      await onSuccess?.(data, variables, context);
    },
    ...mutationOptions,
  });
};
